import DataGrid, { Column, Format } from 'devextreme-react/data-grid';
import { formatNumber } from 'devextreme/localization';
import { useContext } from 'react';
import { useAuth } from '../../../../contexts/auth';
import { CompanyTypes, ProductLine } from '../../../../types/enums';
import { formatShortDate } from '../../../../utils/date-utils';
import { POInvoiceContext } from '../context/POInvoiceContext';
import { PurchaseOrderContext } from '../context/PurchaseOrderContext';
import { POInvoiceProduct } from '../types/POInvoiceProduct';

export default function PurchaseOrderInvoiceProducts() {

    const { order } = useContext(PurchaseOrderContext);
    const { invoice } = useContext(POInvoiceContext);

    const isLaminate = order.details.products.some((p) => {
        return p.productLineID === ProductLine.LaminateInstall || p.productLineID === ProductLine.LaminateDelivered;
    });

    const { user } = useAuth();
    const isServiceProvider = user?.companyTypeId === CompanyTypes.SERVICE_PROVIDER;

    return (
        <>
            <DataGrid
                dataSource={invoice.details.products}
                allowColumnReordering={true}
                allowColumnResizing={true}
                showBorders={true}
                showColumnLines={true}
                columnAutoWidth={true}
                wordWrapEnabled={true}
                keyExpr={'poInvProductID'}
                className="grid-with-header"
            >
                <Column
                    dataField={'sku'}
                    caption={'SKU'}
                    dataType="string"
                />
                <Column
                    dataField={'itemDescription'}
                    caption={'Item Description'}
                    dataType="string"
                    encodeHtml={false}
                    calculateDisplayValue={(rowData: POInvoiceProduct) =>
                        rowData.itemDescription?.replace(new RegExp('\\r\\n', 'g'), '<br/>')
                    }
                />
                <Column
                    dataField={'thickness'}
                    caption={'Thickness'}
                    dataType="string"
                    visible={!isLaminate && !isServiceProvider}
                />
                <Column
                    dataField={'dimensions'}
                    caption={'Dimensions'}
                    dataType="string"
                    visible={!isLaminate && !isServiceProvider}
                />
                <Column
                    dataField={'squareFootage'}
                    caption={'Square Footage'}
                    dataType="string"
                    visible={!isLaminate && !isServiceProvider}
                />
                <Column
                    dataField={'quantity'}
                    caption={'Quantity'}
                    dataType="number"
                />
                <Column
                    dataField={'unitPrice'}
                    caption={'Unit Price'}
                    dataType="number"
                >
                    <Format type="currency" precision={2} ></Format>
                </Column>
                <Column
                    dataField={'freightCost'}
                    caption={'Freight Cost'}
                    dataType="number"
                    visible={!isServiceProvider}
                >
                    <Format type="currency" precision={2} ></Format>
                </Column>
                <Column
                    dataField={'totalCost'}
                    caption={'Total Cost'}
                    dataType="number"
                    calculateCellValue={(row: POInvoiceProduct) => { return row.unitPrice * (row.quantity ?? 0) }}
                >
                    <Format type="currency" precision={2} ></Format>
                </Column>
            </DataGrid>
            <div className='dx-clearfix'></div>
            {
                !!invoice.details.poInvoiceID &&
                <table className='invoice-payment-table'>
                    <tbody>
                        <tr>
                            <td>Check Number:</td>
                            <td>
                                {invoice.details.checkNumber}
                            </td>
                        </tr>
                        <tr>
                            <td>Check Amount:</td>
                            <td>
                                {invoice.details.amountPaid ? formatNumber(invoice.details.amountPaid, { type: 'currency', precision: 2 }) : ""}
                            </td>
                        </tr>
                        <tr>
                            <td>Date Paid:</td>
                            <td>
                                {formatShortDate(invoice.details.datePaid)}
                            </td>
                        </tr>
                    </tbody>
                </table>
            }
            <table className='summary-table'>
                <tbody>
                    <tr>
                        <td>Subtotal:</td>
                        <td>
                            {formatNumber(invoice.details.subTotal, { type: 'currency', precision: 2 })}
                        </td>
                    </tr>
                    <tr>
                        <td>Tax:</td>
                        <td>
                            {formatNumber(invoice.details.tax, { type: 'currency', precision: 2 })}
                        </td>
                    </tr>
                    <tr>
                        <td>Discount:</td>
                        <td>
                            {formatNumber(invoice.details.discount, { type: 'currency', precision: 2 })}
                        </td>
                    </tr>
                    {
                        !isServiceProvider &&
                        <tr>
                            <td>Cut Charge:</td>
                            <td>
                                {formatNumber(invoice.details.cutCharge, { type: 'currency', precision: 2 })}
                            </td>
                        </tr>
                    }
                    {
                        !isServiceProvider &&
                        <tr>
                            <td>Freight:</td>
                            <td>
                                {formatNumber(invoice.details.freightAmount, { type: 'currency', precision: 2 })}
                            </td>
                        </tr>
                    }
                    <tr className='total'>
                        <td>Total:</td>
                        <td>{formatNumber(invoice.details.invoiceTotal, { type: 'currency', precision: 2 })}</td>
                    </tr>
                </tbody>
            </table>
        </>
    );
}
